import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import TextInput from "../../components/TextInput";
import ConfirmPopup from "../../components/ConfirmPopup";
import UnsaveConfirmPopup from "../../pages/action/components/UnsaveConfirmPopup";
import useClickOutside from "../../utils/useOutsideClick";
import CompanyHeader from "components/CompanyHeader";
import CompanySideMenu from "components/CompanySideMenu";
import {useDispatch, useSelector} from "react-redux";
import {ThunkDispatch} from "@reduxjs/toolkit";
import CustomToast from "components/CustomToast";
import {useSnackbar} from "notistack";
import ReturnButton from "../../components/ReturnButton";
import CustomButton from "../../components/CustomButton";
import {LoadingIcon} from "components/Icons";
import NotFoundPage from "../../pages/NotFoundPage";
import Copyright from "../../components/Copyright";
import i18n from 'i18next';
import {
  setTalentTagsList, getTalentTags, createTalentTag,
  setError, setSuccess, setCurrentPage, setSearchQuery,
  deleteTalentTag, getTalentTagDetail, updateTalentTag,
  handleSortByHeader, setIsFromTagsList,
} from "reducers/talentTag";
import { setGlobalSearchTalentFormData, setIsClickSearch, setHaveOtherConditionSearch } from "../../reducers/companyTalent";
import CustomPagination from "../../components/CustomPagination";
import { validateFieldReq } from "../../utils/validation";
import {trimStringFields} from "utils/format";
import { set } from "lodash";
import { setIsFormDirty, setShowLeaveConfirm } from "reducers/app";
import { STAFF_TYPE } from "../../constants/index";

const initFormData = {
  title: "",
  description: "",
}

const initFormError = {
  title: "",
  description: "",
}

const TagsManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fromMenu } = location.state || {};
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const { 
    talentTagsList, isReload, error, success, sortBy, sortOrder, currentPage, pageSize, countItems, searchQuery, talentDetailData, tableHeaders
  } = useSelector((state: any) => state.talentTag);
  const { initSearchTalentForm } = useSelector((state: any) => state.companyTalent);
  const {isFormDirty, showLeaveConfirm, isNavigatingAway} = useSelector((state: any) => state.app);
  const [tagCreating, setTagCreating] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const ref = useRef(null);
  const editRowRef = useRef(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [formData, setFormData] = useState(initFormData);
  const [formError, setFormError] = useState(initFormError);
  const [formEditData, setFormEditData] = useState(initFormData);

  useEffect(() => {
    return () => dispatch(setIsFormDirty(false));
  }, [])

  useEffect(() => {
    fetchTalentTag();
  }, [isReload, sortBy, sortOrder])

  useEffect(() => {
    if (talentDetailData?.id) {
      setFormEditData(talentDetailData);
    }
  }, [talentDetailData])

  const fetchTalentTag = (page=currentPage, search=searchQuery) => {
    let data = {
      page: page,
      pageSize: pageSize,
      searchQuery: search,
      sortBy: sortBy,
      sortOrder: sortOrder,
    }
    data = trimStringFields(data);
    dispatch(getTalentTags(data));
    dispatch(setCurrentPage(page));
  }

  let formErrors = {
    ...initFormError,
  };

  const validateFormData = (formData) => {
    const validateTitleReq = validateFieldReq(formData.title, "タグ名");
    if(validateTitleReq != true) {
      formErrors.title = String(validateTitleReq);
    }

    setFormError(formErrors);

    const errorText: any = Object.entries(formErrors).find(([key, value]) => value !== "");

    return {
      isValid: !Object.values(formErrors).some((error) => error),
      error: errorText && errorText[1] ? errorText[1] : null,
    };
  }

  const handleCreateTagItem  = () => {
    const { isValid, error } = validateFormData(formData);

    if (!isValid) {
      dispatch(setError(error));
      return false
    }

    const data = {
      requestBody: {
        title: formData.title,
        description: formData.description
      }
    }
    dispatch(createTalentTag(data));
    dispatch(setIsFormDirty(false));
    setFormData(initFormData);
    setTagCreating(false);
  }

  const handleCancelCreate = () => {
    setFormData(initFormData);
    setTagCreating(false);
    dispatch(setIsFormDirty(false));
  }

  const handleDeleteTagItem = (tag: any) => {
    setItemDelete(tag)
    setIsConfirmOpen(true);
  };
  
  const handleConfirmDeleteTagItem = () => {
    dispatch(deleteTalentTag({id: itemDelete?.id}));
    setIsConfirmOpen(false);
  }

  const handleEditItem = (tag: any, index) => {
    if (isEdit || tagCreating || staffInfo?.type == STAFF_TYPE.GENERAL) {
      return
    }
    
    dispatch(getTalentTagDetail({id: tag?.id}));
    setIsEdit(true);
    setTagCreating(false);
    setFormError(initFormError);
  };

  const handleUpdateActionItem = () => {
    if (!talentDetailData?.id) {
      return;
    }

    const { isValid, error } = validateFormData(formEditData);

    if (!isValid) {
      dispatch(setError(error));
      return false
    }

    const data = {
      title: formEditData.title,
      description: formEditData.description
    }
    dispatch(updateTalentTag({ id: Number(talentDetailData?.id), requestBody: data }));
    dispatch(setIsFormDirty(false));
    setFormEditData(initFormData);   
    setIsEdit(false); 
  };

  const handleCancelEdit = () => {
    setFormEditData(initFormData);
    setIsEdit(false);
    dispatch(setIsFormDirty(false));
  }

  const handleChangePage = (page: number) => {
    dispatch(setCurrentPage(page))
    fetchTalentTag(page);
  };

  const handleSetSearchQuery = (value: string) => {
    dispatch(setSearchQuery(value));
  };

  const handleSearchTag = (event: any) => {
    if (event.key === "Enter") {
      fetchTalentTag(1);
    }
  };

  const handleClearSearch = () => {
    handleSetSearchQuery("");
    fetchTalentTag(1, "");
  }

  const handleSort = (sortHeader: any) => {
    dispatch(handleSortByHeader(sortHeader));
    dispatch(setCurrentPage(1));
  };

  const handleSelectTag = (tag) => {
    if(isFormDirty) {
      dispatch(setShowLeaveConfirm(true));
      setSelectedItem(tag)
      return;
    }
    handleGoToTalentList(tag)
  }

  const handleGoToTalentList = (tag) => {
    let currentTag = tag
    if(!currentTag && selectedItem) {
      currentTag = selectedItem
    }
    dispatch(setGlobalSearchTalentFormData({
      ...initSearchTalentForm,
      tags: [{id: currentTag?.id, name: currentTag?.title}],
      withdrawn_talent_included: true,
    }))
    dispatch(setHaveOtherConditionSearch(false));
    dispatch(setIsClickSearch(true));
    navigate('/company/talent', { state: { isFromDetail: false } });
  }

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const showCustomToast = ({title="メッセージ", message, variant="success", type="normal"}) => {
    enqueueSnackbar("I love snacks.", {
      content: (key) => (
        <CustomToast
          title={title}
          message={message}
          variant={variant}
          snackbarKey={key}
          closeSnackbar={closeSnackbar}
          type={type}
        />
      ),
    });
  }
  useEffect(() => {
    if(error) {
      showCustomToast({message: error, variant: "error"});
      dispatch(setError(null));
    }
    else if(success) {
      showCustomToast({message: success});
      dispatch(setSuccess(null));
    }
  }, [error, success]);

  const handleClickBackButton = () => {
    if(isFormDirty) {
      dispatch(setShowLeaveConfirm(true));
      return;
    }
    dispatch(setIsFromTagsList(true));
    navigate(-1, { state: { isFromDetail: true } });
  };

  const handleTagCreatingOpen = () => {
    if(isEdit && isFormDirty) {
      dispatch(setShowLeaveConfirm(true));
      return;
    }
    setTagCreating(true);
    setIsEdit(false);
    setFormError(initFormError);
  }

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    markDirty();
    if(isEdit) {
      setFormEditData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  }

  const markDirty = () => {
    if(!isFormDirty) {
      dispatch(setIsFormDirty(true));
    }
  }

  return (
    <>
      <CompanyHeader />
      <div className="mt-6 px-6 flex gap-6 relative">
        <CompanySideMenu />
        <div className="flex-1">
          <div className="mb-4 flex items-center gap-4">
            {!fromMenu && <ReturnButton 
              onClick={() => handleClickBackButton() } />}
            {!fromMenu && <span className="text-sm alu-text-base-1">/</span>}
            <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
              タグ管理
            </h3>
          </div>

          <p className="text-sm alu-text-base-1 mb-6">タレントを分類するためのタグを管理します。タグは自由に作成してタレントに紐づけることができます。</p>

          <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CustomButton
                  type="button"
                  size="sm"
                  customStyle="!w-[72px]"
                  onClick={handleTagCreatingOpen} disabled={tagCreating || staffInfo?.type == STAFF_TYPE.GENERAL}
                >
                  新規作成
                </CustomButton>
              </div>
              <div className='relative'>
                <TextInput
                  value={searchQuery}
                  onChange={(e: any) => handleSetSearchQuery(e.target.value)}
                  onKeyDown={(e: any) => handleSearchTag(e)}
                  onClearQuery={() => handleClearSearch()}
                  placeholder="検索"
                  searchIcon={true}
                  customClassName="!h-[28px] !w-[240px] !text-xs"
                  size={"filter"}
                />
              </div>
            </div>
          </div>
          <div className="bg-white relative mb-6 rounded-b-lg">
            <table className="table-auto w-full">
              <thead className="border-b alu-border px-4 py-2">
                <tr>
                  {tableHeaders.map((header, idx) => (
                    <th key={idx} className={`${idx === 0 ? "pl-6 pr-3" : "px-3"} py-2 text-left ${header?.width ? header.width : ""}`}>
                      <div 
                        className={`flex items-center ${header.isSort && "hover:cursor-pointer"}`}
                        onClick={() => header.isSort && handleSort(header)}
                      >
                        <p className={`text-sm leading-[1.3125] ${header.currentSortState !== 0 ? "font-bold underline underline-offset-4 alu-text-base-2" : "font-normal alu-text-base-1"}`}>{header.title}</p>
                        {header.isSort && (
                          <button className={`${header.currentSortState === 1 ? "rotate-180" : "rotate-0"}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" ><g clipPath="url(#clip0_8574_176)"><path d="M7 10L12 15L17 10H7Z" fill="#808080" /></g><defs><clipPath id="clip0_8574_176"><rect width="24" height="24" fill="white" /></clipPath></defs></svg>
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tagCreating && (
                  <tr className="border border-[#FF861A]" ref={ref}>
                    <td className="pl-5 pr-3 py-[14px] text-left font-normal text-sm leading-[1.3125]">
                      <TextInput name="title" placeholder="入力してください" customClassName="!h-[33px]" value={formData.title} maxLength={15} onChange={handleChange} error={formError.title.length > 0} />
                    </td>
                    <td className="px-3 py-[14px] text-left font-normal text-sm leading-[1.3125]">
                      <TextInput name="description" placeholder="入力してください" customClassName="!h-[33px]" value={formData.description} maxLength={100} onChange={handleChange} />
                    </td>
                    <td></td>
                    <td className="pl-3 pr-4 py-[14px] text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                      <div className="flex items-center gap-4 justify-end">
                        <CustomButton onClick={handleCreateTagItem} type="button" size="sm" customStyle="!w-[59px]">
                          保存
                        </CustomButton>
                        <CustomButton onClick={handleCancelCreate} variant="text" size="sm">
                          キャンセル
                        </CustomButton>
                      </div>
                    </td>
                  </tr>
                )}
                {talentTagsList && talentTagsList.length > 0 && talentTagsList.map((item: any, idx: any) => (
                  <tr key={idx} className={`
                    ${
                      !(talentDetailData?.id === item?.id && isEdit)
                        ? staffInfo?.type == STAFF_TYPE.GENERAL ? "border-t border-t-[#E0E0E0]" : "border-t border-t-[#E0E0E0] hover:bg-[#fff3e5] cursor-pointer"
                        : "outline outline-1 outline-[#FF861A]"
                    }`}
                    ref={talentDetailData?.id === item?.id ? editRowRef : null}>
                    <td onClick={() => handleEditItem(item, idx)} className="pl-6 pr-3 py-4 text-left font-normal text-sm leading-[1.3125]">
                      { isEdit && talentDetailData?.id === item?.id ? (
                          <TextInput name="title" placeholder="入力してください" customClassName="!h-[33px]" value={formEditData?.title} maxLength={15} onChange={handleChange} error={formError.title.length > 0} />
                        ) : (
                          <p className="w-fit px-2 py-[0.125rem] text-center rounded-sm bg-[#F7F7F7] text-sm alu-text-base-2">{item?.title}</p>
                        )
                      }
                    </td>
                    <td onClick={() => handleEditItem(item, idx)} className="px-3 py-4 text-left font-normal text-sm leading-[1.3125]">
                      {isEdit && talentDetailData?.id === item?.id ? (
                        <TextInput name="description" placeholder="入力してください" customClassName="!h-[33px]" value={formEditData?.description} maxLength={100} onChange={handleChange} />
                      ) : (
                        <p className="alu-text-base-2 break-all line-clamp-1">{item?.description}</p>
                      )
                      }
                    </td>
                    <td onClick={() => handleEditItem(item, idx)} className="px-3 py-4 text-left font-normal text-sm leading-[1.3125]">
                      <p className="w-fit alu-text-base-2 underline hover:cursor-pointer hover:text-[#FF861A]"
                        onClick={(e) => {e.stopPropagation(); handleSelectTag(item)}}
                      >
                        {item?.number_of_talents}
                      </p>
                    </td>
                    <td className="pl-3 pr-4 py-4 text-right font-normal text-sm leading-[1.3125]">
                      {isEdit && talentDetailData?.id === item?.id ? (
                          <div className="flex items-center gap-4 justify-end">
                            <CustomButton onClick={handleUpdateActionItem} type="button" size="sm" customStyle="!w-[59px]">
                              保存
                            </CustomButton>
                            <CustomButton onClick={handleCancelEdit} variant="text">
                              キャンセル
                            </CustomButton>
                          </div>
                        ) : (
                          <CustomButton customStyle="ml-auto" 
                            onClick={() => handleDeleteTagItem(item)} variant="text"
                            disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
                            >
                            削除
                          </CustomButton>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {countItems > pageSize &&
            <div className="flex items-center justify-center mt-6">
              <CustomPagination
                total={countItems}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={handleChangePage}
              />
            </div>}
        </div>
      </div>
      {isConfirmOpen && (
        <ConfirmPopup open={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          confirmContent={`タグ「${itemDelete?.title}」を削除しますか？`}
          confirmDescription={`このタグを削除すると、すべてのタレントでこのタグが表示されなくなります。削除してもよろしいですか。`}
          onConfirm={handleConfirmDeleteTagItem}
          type="delete"
        />
      )}
      {showLeaveConfirm && !isNavigatingAway && (
        <UnsaveConfirmPopup
          open={showLeaveConfirm}
          onClose={() => dispatch(setShowLeaveConfirm(false))}
          confirmContent="編集中の内容を保存せずに終了しますか？"
          onOtherAction={() => {
            setFormData(initFormData);
            setFormEditData(initFormData);
            setTagCreating(false);
            setIsEdit(false);
            dispatch(setIsFormDirty(false));
            dispatch(setShowLeaveConfirm(false));
            handleGoToTalentList(null);
          }}
        />
      )}
      <Copyright view="company" />
    </>
  );
};

export default TagsManagement;